import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Ban, Filter, Loader2, Search } from 'lucide-react';
import api, { Metric } from '@/lib/api';
import SeverityBadge from '@/components/ui/SeverityBadge';
import RecommendedQtyCell from '@/components/ui/RecommendedQtyCell';

function formatDate(dateStr: string | null) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export default function ExcludedProducts() {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState<Metric[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [excludeSavingSku, setExcludeSavingSku] = useState<string | null>(null);
  const [excludeMessage, setExcludeMessage] = useState('');

  const loadData = async () => {
    try {
      const data = await api.getExcludedMetrics();
      setMetrics(data);
    } catch (err) {
      console.error('Fehler beim Laden ausgeschlossener Artikel:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredMetrics = metrics.filter((metric) => {
    const matchesSeverity = !severityFilter || metric.severity === severityFilter;
    const query = searchQuery.trim().toLowerCase();
    const matchesSearch = query.length < 3
      || metric.sku.toLowerCase().includes(query)
      || metric.name.toLowerCase().includes(query);

    return matchesSeverity && matchesSearch;
  });

  const handleExcludeToggle = async (sku: string, isExcludedFromPlanning: boolean) => {
    setExcludeSavingSku(sku);
    setExcludeMessage('');

    try {
      await api.updateProductOverrides(sku, { isExcludedFromPlanning });
      setExcludeMessage('Artikel wurde erfolgreich aktualisiert.');
      await loadData();
    } catch (err) {
      console.error('Aktualisierung fehlgeschlagen:', err);
      setExcludeMessage('Fehler beim Speichern. Bitte erneut versuchen.');
    } finally {
      setExcludeSavingSku(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-brand" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Ban className="h-8 w-8 text-brand" />
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-ink">Ausgeschlossene Artikel</h1>
          <p className="text-[12.5px] text-ink-soft mt-1">Alle Artikel mit Status „von Planung ausgeschlossen".</p>
        </div>
      </div>

      <div className="rounded-card border border-line bg-surface overflow-hidden">
        <div className="px-5 py-4 border-b border-line flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-[16.5px] font-extrabold tracking-tight text-ink">Artikelliste</h2>
            {excludeMessage && (
              <p className="font-mono text-[11px] text-ink-muted mt-0.5">{excludeMessage}</p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-soft" />
              <input
                type="text"
                placeholder="SKU oder Name suchen..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input-field pl-9 w-48"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-soft" />
              <select
                value={severityFilter}
                onChange={(e) => setSeverityFilter(e.target.value)}
                className="input-field pl-9 pr-8 appearance-none"
              >
                <option value="">Alle</option>
                <option value="red">Kritisch</option>
                <option value="yellow">Warnung</option>
                <option value="green">OK</option>
              </select>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-surface-3 text-left">
                <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft">SKU</th>
                <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft">Produkt</th>
                <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft">Status</th>
                <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft text-right">Reichweite</th>
                <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft text-right">OOS Datum</th>
                <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft text-right">Bestand</th>
                <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft text-right">Bestellt</th>
                <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft text-right">Empf. Menge</th>
                <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft text-right">Tägl. Verbrauch</th>
                <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft text-right">Von Planung ausschließen</th>
              </tr>
            </thead>
            <tbody>
              {filteredMetrics.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-12 text-center font-mono text-[12.5px] text-ink-soft">
                    Keine ausgeschlossenen Artikel gefunden.
                  </td>
                </tr>
              ) : (
                filteredMetrics.map((metric) => (
                  <tr
                    key={metric.sku}
                    onClick={() => navigate(`/products/${encodeURIComponent(metric.sku)}`)}
                    className="cursor-pointer border-b border-surface-3 hover:bg-surface-2 transition-colors"
                  >
                    <td className="px-4 py-3 font-mono text-[10.5px] text-ink-soft">{metric.sku}</td>
                    <td className="px-4 py-3 text-[13.5px] font-bold text-ink">{metric.name}</td>
                    <td className="px-4 py-3"><SeverityBadge severity={metric.severity} /></td>
                    <td className="px-4 py-3 text-right font-mono text-[12.5px] text-ink">{metric.coverageDays.toFixed(0)} Tage</td>
                    <td className="px-4 py-3 text-right font-mono text-[12.5px] text-ink">{formatDate(metric.oosDate)}</td>
                    <td className="px-4 py-3 text-right font-mono text-[12.5px] text-ink">{metric.onHand.toLocaleString('de-DE')}</td>
                    <td className="px-4 py-3 text-right font-mono text-[12.5px] text-ink">{metric.onOrder.toLocaleString('de-DE')}</td>
                    <td className="px-4 py-3 text-right font-mono text-[12.5px] font-bold text-ink">
                      <RecommendedQtyCell qty={metric.recommendedOrderQty} severity={metric.severity} />
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-[12.5px] text-ink">{metric.dailyUsage.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right">
                      <label className="inline-flex items-center gap-2 text-[12.5px] text-ink-muted cursor-pointer" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked
                          disabled={excludeSavingSku === metric.sku}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => handleExcludeToggle(metric.sku, e.target.checked)}
                        />
                        {excludeSavingSku === metric.sku ? <Loader2 className="h-3 w-3 animate-spin text-brand" /> : null}
                      </label>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
